import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Rating,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt, accentGradientDark } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { StatusChip } from "../../../shared/components/chips/StatusChip";
import receivedOffersApi from "../api/receivedOffersApi";
import serviceReviewsApi from "../api/serviceReviewsApi";
import { CustomerReviewSummary } from "../models/reviewModels";
import { ReceivedOffer, OfferStatusCode } from "../models/receivedOffersModels";
import {
  CompletionTimeCode,
  WarrantyPeriodCode,
  OfferPaymentTermsCode,
  OfferFile,
} from "../../serviceOffers/models/models";
import {
  createReceivedOfferStatusLabelMap,
  createReceivedOfferStatusColorMap,
} from "../utils/receivedOfferStatusMaps";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { ImagePreviewStrip, useGalleryImages } from "../../../shared/components/gallery";

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (file: { name: string; content: string }): boolean => {
  return IMAGE_EXTENSIONS.test(file.name) || file.content?.startsWith("data:image/");
};

// ===================================================================
//  Props
// ===================================================================

interface CompareOffersDialogProps {
  open: boolean;
  serviceRequestId: string;
  serviceRequestIssue: string;
  onClose: () => void;
  onOfferAccepted: () => void;
}

// ===================================================================
//  Helpers
// ===================================================================

const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return "\u2014";
  return `\u20AC${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const canTakeAction = (status: OfferStatusCode): boolean =>
  status === OfferStatusCode.sent ||
  status === OfferStatusCode.viewed ||
  status === OfferStatusCode.negotiating;

const includedServiceKeys: { key: keyof ReceivedOffer; translationKey: string }[] = [
  { key: "includeDiagnostics", translationKey: "serviceOffers:includeDiagnostics" },
  { key: "includeLaborForRepair", translationKey: "serviceOffers:includeLaborForRepair" },
  { key: "includePartsAndMaterials", translationKey: "serviceOffers:includePartsAndMaterials" },
  { key: "includeTestingAndCalibration", translationKey: "serviceOffers:includeTestingAndCalibration" },
  { key: "includeWorkAreaCleanup", translationKey: "serviceOffers:includeWorkAreaCleanup" },
  { key: "includeDetailedServiceReport", translationKey: "serviceOffers:includeDetailedServiceReport" },
];

// Resolves file content — fetches full offer if content missing from list response
const resolveFileContent = async (
  file: OfferFile,
  serviceRequestId: string,
  offerId: string,
): Promise<string> => {
  let content = file.content;
  if (!content) {
    try {
      const fullOffer = await receivedOffersApi.getReceivedOffer(serviceRequestId, offerId);
      const fullFile = fullOffer.files?.find((f) => f.name === file.name);
      content = fullFile?.content || "";
    } catch (fetchErr) {
      console.error("Failed to fetch full offer for file download:", fetchErr);
    }
  }
  if (!content) return "";

  // Content can be: a URL (https://...), a data URI (data:...), or raw base64
  // If it's a URL, use it directly. If it's a data URI, use as-is. Otherwise prepend data URI prefix.
  if (content.startsWith("http://") || content.startsWith("https://") || content.startsWith("data:")) {
    return content;
  }

  // Raw base64 — prepend data URI prefix
  const ext = file.name.split(".").pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
    gif: "image/gif", webp: "image/webp", doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv", txt: "text/plain",
  };
  const mime = mimeTypes[ext || ""] || "application/octet-stream";
  return `data:${mime};base64,${content}`;
};

// Download file: forces save-to-disk
const downloadOfferFile = async (file: OfferFile, serviceRequestId: string, offerId: string) => {
  try {
    const content = await resolveFileContent(file, serviceRequestId, offerId);
    if (!content) return;
    const link = document.createElement("a");
    link.href = content;
    link.download = file.name;
    link.click();
    link.remove();
  } catch (err) {
    console.error("File download failed:", err, "File:", file);
  }
};

// ===================================================================
//  Section header — icon + uppercase label (ConfirmServiceDialog pattern)
// ===================================================================

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  action?: React.ReactNode;
}> = ({ icon, label, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0.75,
      mb: "6px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {icon}
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          color: dt.textMuted,
          fontFamily: dt.font,
        }}
      >
        {label}
      </Box>
    </Box>
    {action}
  </Box>
);

// ===================================================================
//  CollapsibleText — shows 1 line with fade, expands on toggle
// ===================================================================

interface CollapsibleTextProps {
  text: string;
  translate: (key: string) => any;
  bgColor?: string;
  borderLeftColor?: string;
  italic?: boolean;
}

const CollapsibleText: React.FC<CollapsibleTextProps> = ({ text, translate, bgColor, borderLeftColor, italic }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      // Check if text overflows 1.5em (one line)
      setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
    }
  }, [text]);

  return (
    <Box>
      <Box
        ref={textRef}
        sx={{
          fontSize: "12px",
          color: "#475569",
          fontFamily: dt.font,
          lineHeight: 1.5,
          position: "relative",
          ...(bgColor && {
            backgroundColor: bgColor,
            borderRadius: "6px",
            padding: "8px 12px",
          }),
          ...(borderLeftColor && {
            borderLeft: `3px solid ${borderLeftColor}`,
          }),
          ...(italic && { fontStyle: "italic" }),
          ...(!expanded && {
            maxHeight: "1.5em",
            overflow: "hidden",
          }),
          ...(!expanded && isOverflowing && {
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "40%",
              height: "1.5em",
              background: `linear-gradient(to right, transparent, ${bgColor || "white"})`,
              pointerEvents: "none",
            },
          }),
        }}
      >
        {text}
      </Box>
      {isOverflowing && (
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{ fontSize: "11px", color: dt.blue, cursor: "pointer", fontWeight: 500, mt: 0.25, "&:hover": { textDecoration: "underline" } }}
        >
          {expanded ? translate("common:showLess") : translate("common:showMore")}
        </Box>
      )}
    </Box>
  );
};

// ===================================================================
//  OfferCompareCard (single offer card inside the dialog)
// ===================================================================

interface OfferCompareCardProps {
  offer: ReceivedOffer;
  serviceRequestId: string;
  translate: (key: string, opts?: any) => any;
  navigate: ReturnType<typeof useNavigate>;
  onAccept: (offerId: string) => void;
  onReject: (offerId: string, reason?: string) => void;
  actionLoading: boolean;
  index: number;
}

const OfferCompareCard: React.FC<OfferCompareCardProps> = ({
  offer,
  serviceRequestId,
  translate,
  navigate,
  onAccept,
  onReject,
  actionLoading,
  index,
}) => {
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [providerReview, setProviderReview] = useState<CustomerReviewSummary | null>(null);
  const confirm = useConfirmation();

  // Image gallery for offer attachments
  const imageFiles = useMemo(() => (offer.files || []).filter(isImageFile), [offer.files]);
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  useEffect(() => {
    if (offer.provider?.id) {
      serviceReviewsApi
        .getReviewSummary(offer.provider.id)
        .then((summary) => {
          if (summary.totalCount > 0) setProviderReview(summary);
        })
        .catch(() => {});
    }
  }, [offer.provider?.id]);

  const formatCompletionTime = (code?: CompletionTimeCode, custom?: string): string => {
    if (!code) return "\u2014";
    if (code === CompletionTimeCode.custom) return custom || "\u2014";
    return translate(`serviceOffers:completionTimeCodes.${CompletionTimeCode[code]}`);
  };

  const formatWarranty = (code?: WarrantyPeriodCode, custom?: string): string => {
    if (!code) return "\u2014";
    if (code === WarrantyPeriodCode.custom) return custom || "\u2014";
    return translate(`serviceOffers:warrantyPeriodCodes.${WarrantyPeriodCode[code]}`);
  };

  const formatPaymentTerms = (code?: OfferPaymentTermsCode, custom?: string): string => {
    if (!code) return "\u2014";
    if (code === OfferPaymentTermsCode.custom) return custom || "\u2014";
    return translate(`serviceOffers:paymentTermsCodes.${OfferPaymentTermsCode[code]}`);
  };

  const handleAccept = () => {
    confirm({
      title: translate("servicing:acceptOfferTitle"),
      text: translate("servicing:compareOffers.acceptConfirm", { provider: offer.provider.name }),
      confirmButton: translate("servicing:yesAccept"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: () => onAccept(offer.id),
    });
  };

  const handleRejectConfirm = () => {
    onReject(offer.id, rejectReason.trim() || undefined);
    setRejectOpen(false);
    setRejectReason("");
  };

  const isAccepted = offer.statusCode === OfferStatusCode.accepted ||
    offer.statusCode === OfferStatusCode.awaitingConfirmation ||
    offer.statusCode === OfferStatusCode.confirmed ||
    offer.statusCode === OfferStatusCode.workInProgress ||
    offer.statusCode === OfferStatusCode.workCompleted ||
    offer.statusCode === OfferStatusCode.serviceCompleted;

  const isRejected = offer.statusCode === OfferStatusCode.rejected;

  // Calculate hourly rate
  const hourlyRate = (offer.laborHours && offer.laborCost)
    ? offer.laborCost / offer.laborHours
    : undefined;

  // Per-km and per-hour travel rates
  const travelDistanceKm = offer.travelDistanceKm;
  const travelRatePerKm = offer.travelRatePerKm;
  const travelTimeHours = offer.travelTimeHours;
  const travelRatePerHour = offer.travelRatePerHour;

  return (
    <Box
      sx={{
        flex: "0 0 340px",
        scrollSnapAlign: "start",
        width: { xs: "100%", md: "340px" },
        minWidth: { xs: "100%", md: "340px" },
        backgroundColor: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.08}s both`,
        "&:hover": {
          boxShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
          transform: "translateY(-3px)",
        },
        ...(isAccepted && {
          borderColor: "#27AE60",
          borderWidth: "2px",
          boxShadow: "0 0 0 3px rgba(39,174,96,0.08), 0 4px 16px rgba(0,0,0,0.06)",
        }),
      }}
    >
      {/* PROVIDER HEADER — gradient background zone */}
      <Box
        sx={{
          background: isAccepted
            ? "linear-gradient(135deg, rgba(39,174,96,0.04) 0%, rgba(39,174,96,0.08) 100%)"
            : "linear-gradient(135deg, rgba(52,152,219,0.03) 0%, rgba(52,152,219,0.07) 100%)",
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${dt.cardBorder}`,
        }}
      >
        {/* Status chip row — hidden for "Viewed" (shown as inline eye icon instead) */}
        {offer.statusCode !== OfferStatusCode.viewed && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <StatusChip
              status={offer.statusCode}
              colorMap={createReceivedOfferStatusColorMap()}
              labelMap={createReceivedOfferStatusLabelMap(translate)}
            />
          </Box>
        )}

        {/* Provider info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ...(offer.statusCode === OfferStatusCode.viewed && { mt: 0.5 }) }}>
          {offer.provider.logoUrl ? (
            <Box
              component="img"
              src={offer.provider.logoUrl}
              alt={offer.provider.name}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                objectFit: "cover",
                border: `2px solid ${dt.cardBg}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: accentGradientDark,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              }}
            >
              <Box sx={{ fontSize: "16px", fontWeight: 700, color: "#FFFFFF", fontFamily: dt.font }}>
                {offer.provider.name?.charAt(0)?.toUpperCase() || "?"}
              </Box>
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (offer.provider?.id) navigate(`/companies/${offer.provider.id}`);
                }}
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                  "&:hover": { color: dt.blue },
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {offer.provider.name}
              </Box>
              {offer.statusCode === OfferStatusCode.viewed && (
                <Tooltip title={translate("servicing:compareOffers.viewed") || "Viewed"} arrow placement="top">
                  <VisibilityOutlinedIcon sx={{ fontSize: 16, color: dt.textMuted, flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
            {providerReview && (
              <Tooltip
                title={translate("servicing:reviews.ratingTooltip", {
                  rating: providerReview.averageRating.toFixed(1),
                  count: providerReview.totalCount,
                })}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                  <Rating
                    value={providerReview.averageRating}
                    readOnly
                    precision={0.1}
                    size="small"
                    sx={{ fontSize: "14px", "& .MuiRating-iconFilled": { color: "#FFB400" } }}
                  />
                  <Box sx={{ fontSize: "11px", color: dt.textMuted, fontWeight: 600, fontFamily: dt.fontMono }}>
                    ({providerReview.totalCount})
                  </Box>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* CARD BODY */}
      <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
        {/* 2. PRICE (prominent) */}
        <Box>
          <SectionHeader
            icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
            label={translate("servicing:compareOffers.price")}
          />
          <Box sx={{ fontSize: "24px", fontWeight: 700, fontFamily: dt.fontMono, color: "#0F172A", letterSpacing: "-0.5px" }}>
            {formatCurrency(offer.totalEstimatedCost)}
          </Box>
          {/* Breakdown */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", mt: 0.5 }}>
            {offer.laborCost != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>
                  {translate("servicing:compareOffers.labor")}{offer.laborHours ? ` (${offer.laborHours}h)` : ""}
                </Box>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <Box sx={{ color: dt.textSecondary, fontFamily: dt.fontMono, fontWeight: 700 }}>{formatCurrency(offer.laborCost)}</Box>
                  {hourlyRate != null && (
                    <Box sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "10px" }}>
                      ({formatCurrency(hourlyRate)}/h)
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {offer.partsCost != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.parts")}</Box>
                <Box sx={{ color: dt.textSecondary, fontFamily: dt.fontMono, fontWeight: 700 }}>{formatCurrency(offer.partsCost)}</Box>
              </Box>
            )}
            {offer.travelCost != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>
                  {translate("servicing:compareOffers.travel")}
                  {travelDistanceKm != null || travelTimeHours != null
                    ? ` (${[
                        travelDistanceKm != null ? `${travelDistanceKm} km` : null,
                        travelTimeHours != null ? `${travelTimeHours}h` : null,
                      ].filter(Boolean).join(", ")})`
                    : ""}
                </Box>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <Box sx={{ color: dt.textSecondary, fontFamily: dt.fontMono, fontWeight: 700 }}>{formatCurrency(offer.travelCost)}</Box>
                  {(travelRatePerKm != null || travelRatePerHour != null) && (
                    <Box sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "10px" }}>
                      ({[
                        travelRatePerKm != null ? `${formatCurrency(travelRatePerKm)}/km` : null,
                        travelRatePerHour != null ? `${formatCurrency(travelRatePerHour)}/h` : null,
                      ].filter(Boolean).join(" \u00B7 ")})
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {offer.additionalCosts != null && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.additional")}</Box>
                <Box sx={{ color: dt.textSecondary, fontFamily: dt.fontMono, fontWeight: 700 }}>{formatCurrency(offer.additionalCosts)}</Box>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: dt.rowBorder }} />

        {/* 3. TIMELINE */}
        <Box>
          <SectionHeader
            icon={<ScheduleOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
            label={translate("servicing:compareOffers.timeline")}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            {offer.earliestStartDate && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.earliestStart")}</Box>
                <Box sx={{ color: dt.textSecondary, fontWeight: 600, fontFamily: dt.fontMono, fontSize: "11px" }}>{dayjs(offer.earliestStartDate).format("MMM DD, YYYY")}</Box>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
              <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.completionTime")}</Box>
              <Box sx={{ color: dt.textSecondary, fontWeight: 600 }}>{formatCompletionTime(offer.completionTimeCode, offer.customCompletionTime)}</Box>
            </Box>
            {offer.estimatedDuration && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("serviceOffers:estimatedDuration")}</Box>
                <Box sx={{ color: dt.textSecondary, fontWeight: 600 }}>{offer.estimatedDuration}</Box>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: dt.rowBorder }} />

        {/* 4. SERVICE APPROACH — collapsible, 1-line default with fade */}
        {offer.serviceApproach && (
          <>
            <Box>
              <SectionHeader
                icon={<BuildOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
                label={translate("servicing:compareOffers.serviceApproach")}
              />
              <CollapsibleText text={offer.serviceApproach} translate={translate} />
            </Box>
            <Divider sx={{ borderColor: dt.rowBorder }} />
          </>
        )}

        {/* 5. MESSAGE FROM PROVIDER — collapsible, 1-line default with fade */}
        {offer.messageToCustomer && (
          <>
            <Box>
              <SectionHeader
                icon={<MessageOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
                label={translate("servicing:compareOffers.messageFromProvider")}
              />
              <CollapsibleText
                text={offer.messageToCustomer}
                translate={translate}
                bgColor="#F8FAFC"
                borderLeftColor={dt.blue}
                italic
              />
            </Box>
            <Divider sx={{ borderColor: dt.rowBorder }} />
          </>
        )}

        {/* 6. INCLUDED SERVICES — collapsible, default collapsed */}
        <Box>
          <Box
            onClick={() => setServicesExpanded(!servicesExpanded)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
              borderRadius: dt.radiusSm,
              px: 0.5,
              py: 0.25,
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: dt.blueSoft },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <ChecklistOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />
              <Box sx={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.2px", color: dt.textMuted, fontFamily: dt.font }}>
                {translate("servicing:compareOffers.includedServices")}
              </Box>
            </Box>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: servicesExpanded ? dt.blueSoft : "transparent",
                transition: "all 0.2s ease",
              }}
            >
              {servicesExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 16, color: dt.textMuted }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color: dt.textMuted }} />
              )}
            </Box>
          </Box>
          <Collapse in={servicesExpanded} timeout={200}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", mt: "6px", pl: 0.5 }}>
              {includedServiceKeys.map((service) => (
                <Box key={service.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {offer[service.key] ? (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        backgroundColor: "rgba(39,174,96,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 12, color: "#27AE60" }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        backgroundColor: "#F3F4F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 12, color: "#D1D5DB" }} />
                    </Box>
                  )}
                  <Box sx={{ fontSize: "12px", color: offer[service.key] ? "#374151" : "#9CA3AF", fontFamily: dt.font }}>
                    {translate(service.translationKey)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ borderColor: dt.rowBorder }} />

        {/* 7. TERMS */}
        <Box>
          <SectionHeader
            icon={<GavelOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
            label={translate("servicing:compareOffers.warranty")}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
              <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.warranty")}</Box>
              <Box sx={{ color: dt.textSecondary, fontWeight: 600 }}>{formatWarranty(offer.warrantyPeriodCode, offer.customWarrantyPeriod)}</Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontFamily: dt.font }}>
              <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.paymentTerms")}</Box>
              <Box sx={{ color: dt.textSecondary, fontWeight: 600 }}>{formatPaymentTerms(offer.paymentTermsCode, offer.customPaymentTerms)}</Box>
            </Box>
          </Box>
        </Box>

        {/* 8. FILE ATTACHMENTS */}
        {offer.files && offer.files.length > 0 && (
          <>
            <Divider sx={{ borderColor: dt.rowBorder }} />
            <Box>
              <SectionHeader
                icon={<AttachFileOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
                label={`${translate("servicing:compareOffers.attachments")} (${offer.files.length})`}
              />
              {galleryImages.length > 0 && (
                <Box sx={{ mb: "6px" }}>
                  <ImagePreviewStrip images={galleryImages} maxVisible={3} />
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {offer.files.map((file, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: "6px 10px",
                      borderRadius: dt.radiusSm,
                      backgroundColor: "#FAFBFC",
                      border: `1px solid ${dt.rowBorder}`,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        backgroundColor: dt.blueSoft,
                        borderColor: dt.cardBorder,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        backgroundColor: dt.blueSoft,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 15, color: dt.blue }} />
                    </Box>
                    <Box
                      sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => downloadOfferFile(file, serviceRequestId, offer.id)}
                    >
                      <Box sx={{ fontSize: "12px", fontWeight: 500, color: dt.textLink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: dt.font, "&:hover": { textDecoration: "underline" } }}>
                        {file.name}
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.fontMono, fontWeight: 500, flexShrink: 0 }}>
                      {formatFileSize(file.size)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ borderColor: dt.rowBorder }} />

        {/* 9. VALIDITY */}
        <Box>
          <SectionHeader
            icon={<EventOutlinedIcon sx={{ fontSize: 14, color: dt.textMuted }} />}
            label={translate("servicing:sentDate")}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            {offer.sentDate && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "11px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("servicing:sentDate")}</Box>
                <Box sx={{ color: dt.textSecondary, fontWeight: 600, fontFamily: dt.fontMono }}>{dayjs(offer.sentDate).format("MMM DD, YYYY")}</Box>
              </Box>
            )}
            {offer.expiresAt && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "11px", fontFamily: dt.font }}>
                <Box sx={{ color: dt.textMuted }}>{translate("servicing:compareOffers.validity")}</Box>
                <Box sx={{
                  color: dayjs(offer.expiresAt).isBefore(dayjs()) ? "#E74C3C" : dt.textSecondary,
                  fontWeight: 600,
                  fontFamily: dt.fontMono,
                  ...(dayjs(offer.expiresAt).isBefore(dayjs()) && {
                    backgroundColor: "rgba(231,76,60,0.06)",
                    padding: "1px 6px",
                    borderRadius: "4px",
                  }),
                }}>
                  {dayjs(offer.expiresAt).format("MMM DD, YYYY")}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 10. ACTIONS — elevated footer */}
      <Box
        sx={{
          borderTop: `1px solid ${dt.cardBorder}`,
          background: "linear-gradient(180deg, #FAFBFC 0%, #F3F4F6 100%)",
          padding: "14px 18px",
          mt: "auto",
        }}
      >
        {canTakeAction(offer.statusCode) ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />}
              onClick={handleAccept}
              disabled={actionLoading}
              sx={{
                background: "linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                fontFamily: dt.font,
                py: 1,
                boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(39,174,96,0.3)",
                },
              }}
            >
              {translate("servicing:compareOffers.accept")}
            </Button>

            {!rejectOpen ? (
              <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<CancelOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => setRejectOpen(true)}
                disabled={actionLoading}
                sx={{
                  borderColor: "#E5E7EB",
                  color: "#9CA3AF",
                  borderRadius: dt.radiusPill,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  fontFamily: dt.font,
                  py: 0.75,
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "#E74C3C", color: "#E74C3C", backgroundColor: "rgba(231,76,60,0.04)" },
                }}
              >
                {translate("servicing:compareOffers.reject")}
              </Button>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder={translate("servicing:compareOffers.rejectReason")}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "12px",
                      fontFamily: dt.font,
                      borderRadius: dt.radiusSm,
                      backgroundColor: dt.cardBg,
                      "& fieldset": { borderColor: dt.cardBorder },
                      "&:hover fieldset": { borderColor: dt.blue },
                      "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
                    },
                  }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                    sx={{ flex: 1, textTransform: "none", fontSize: "12px", fontFamily: dt.font, borderRadius: dt.radiusSm, color: dt.textSecondary }}
                  >
                    {translate("common:buttons.cancel")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleRejectConfirm}
                    disabled={actionLoading}
                    sx={{
                      flex: 1,
                      backgroundColor: "#E74C3C",
                      textTransform: "none",
                      fontSize: "12px",
                      fontFamily: dt.font,
                      borderRadius: dt.radiusSm,
                      "&:hover": { backgroundColor: "#C0392B" },
                    }}
                  >
                    {translate("servicing:yesReject")}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : isAccepted ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              color: "#27AE60",
              fontSize: "13px",
              fontWeight: 700,
              fontFamily: dt.font,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(39,174,96,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
            </Box>
            {translate("servicing:compareOffers.accepted")}
          </Box>
        ) : isRejected ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              color: "#9CA3AF",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CancelOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            {translate("servicing:compareOffers.rejected")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

// ===================================================================
//  CompareOffersDialog
// ===================================================================

const CompareOffersDialog: React.FC<CompareOffersDialogProps> = ({
  open,
  serviceRequestId,
  serviceRequestIssue,
  onClose,
  onOfferAccepted,
}) => {
  useRedesignSetup();
  const { translate } = useTranslation(["servicing", "serviceOffers", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");

  const [offers, setOffers] = useState<ReceivedOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Scroll container ref + arrow visibility
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "compare-offers-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const updateScrollArrows = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    if (!isPhone && offers.length > 0) {
      // Wait for render, then check scroll
      const timer = setTimeout(updateScrollArrows, 100);
      return () => clearTimeout(timer);
    }
  }, [offers, isPhone, updateScrollArrows]);

  // Fetch offers when dialog opens
  useEffect(() => {
    if (open && serviceRequestId) {
      setLoading(true);
      receivedOffersApi
        .getReceivedOffers(serviceRequestId, { pageNo: 1, pageSize: 50 })
        .then((page) => {
          setOffers(page.items || []);
        })
        .catch(() => {
          setOffers([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, serviceRequestId]);

  const handleAccept = async (offerId: string) => {
    setActionLoading(true);
    try {
      await receivedOffersApi.acceptOffer(serviceRequestId, offerId);
      onClose();
      onOfferAccepted();
    } catch {
      // Refresh offers to show current state
      try {
        const page = await receivedOffersApi.getReceivedOffers(serviceRequestId, { pageNo: 1, pageSize: 50 });
        setOffers(page.items || []);
      } catch {}
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (offerId: string, reason?: string) => {
    setActionLoading(true);
    try {
      await receivedOffersApi.rejectOffer(serviceRequestId, offerId, reason);
      // Refresh offers
      const page = await receivedOffersApi.getReceivedOffers(serviceRequestId, { pageNo: 1, pageSize: 50 });
      setOffers(page.items || []);
    } catch {
      // Silently fail
    } finally {
      setActionLoading(false);
    }
  };

  const scrollBy = (direction: "left" | "right") => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -360 : 360, behavior: "smooth" });
  };

  // Keyboard arrow support
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") scrollBy("left");
    if (e.key === "ArrowRight") scrollBy("right");
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radiusLg,
          maxHeight: isPhone ? "100%" : "90vh",
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
    >
      {/* BLUE GRADIENT ACCENT BAR (static) */}
      <Box
        sx={{
          height: "4px",
          background: `linear-gradient(90deg, ${dt.blue} 0%, ${dt.gradientDark} 40%, ${dt.gradientLight} 70%, ${dt.blue} 100%)`,
        }}
      />

      {/* TITLE — single-line: title+chip left, icon buttons right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: dt.font,
          fontWeight: 700,
          fontSize: { xs: "15px", md: "17px" },
          color: dt.textPrimary,
          borderBottom: `1px solid ${dt.cardBorder}`,
          py: 1.25,
          px: { xs: 2, md: 3 },
        }}
      >
        {/* Left: title + count chip */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
          <Box sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 700,
            letterSpacing: "-0.3px",
          }}>
            {translate(
              offers.length === 1
                ? "servicing:compareOffers.reviewTitle"
                : "servicing:compareOffers.title"
            )}
          </Box>
          {!loading && (
            <Chip
              label={offers.length}
              size="small"
              sx={{
                backgroundColor: dt.blueSoft,
                color: dt.blue,
                fontWeight: 700,
                fontFamily: dt.fontMono,
                fontSize: "11px",
                height: "22px",
                minWidth: "22px",
                flexShrink: 0,
                border: `1px solid ${dt.blueGlow}`,
              }}
            />
          )}
        </Box>
        {/* Right: action icon buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0, ml: 1.5 }}>
          <Tooltip title={translate("servicing:compareOffers.viewFullDetails")} arrow placement="bottom">
            <IconButton
              size="small"
              onClick={() => {
                onClose();
                navigate(`/service-board/${serviceRequestId}`);
              }}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                color: dt.textMuted,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: dt.blueSoft,
                  color: dt.blue,
                },
              }}
            >
              <ArrowOutwardRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              color: "#C0C5CE",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                color: dt.textSecondary,
              },
            }}
          >
            <CloseOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* CONTENT */}
      <DialogContent
        sx={{
          px: { xs: 1.5, md: 3 },
          py: 2.5,
          backgroundColor: dt.pageBg,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
          backgroundSize: "28px 28px",
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "200px", gap: 1.5 }}>
            <CircularProgress size={32} sx={{ color: dt.blue }} />
            <Box sx={{ fontSize: "13px", color: dt.textMuted, fontFamily: dt.font }}>
              {translate("common:common.loading")}
            </Box>
          </Box>
        ) : offers.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "200px", gap: 1 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: dt.inputBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.5,
            }}>
              <CompareArrowsIcon sx={{ fontSize: 24, color: dt.textMuted }} />
            </Box>
            <Box sx={{ fontSize: "14px", color: dt.textMuted, fontFamily: dt.font }}>
              {translate("servicing:compareOffers.noOffers")}
            </Box>
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            {/* Left arrow button */}
            {!isPhone && canScrollLeft && (
              <IconButton
                onClick={() => scrollBy("left")}
                sx={{
                  position: "absolute",
                  left: -6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: dt.cardBg,
                  border: `1px solid ${dt.cardBorder}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  zIndex: 2,
                  width: 36,
                  height: 36,
                  transition: "all 0.2s ease",
                  "&:hover": { bgcolor: dt.blueSoft, borderColor: dt.blue, color: dt.blue },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            {/* Right arrow button */}
            {!isPhone && canScrollRight && (
              <IconButton
                onClick={() => scrollBy("right")}
                sx={{
                  position: "absolute",
                  right: -6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: dt.cardBg,
                  border: `1px solid ${dt.cardBorder}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  zIndex: 2,
                  width: 36,
                  height: 36,
                  transition: "all 0.2s ease",
                  "&:hover": { bgcolor: dt.blueSoft, borderColor: dt.blue, color: dt.blue },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}

            {/* Scroll-snap container */}
            <Box
              ref={scrollContainerRef}
              onScroll={updateScrollArrows}
              sx={{
                display: "flex",
                flexDirection: isPhone ? "column" : "row",
                gap: 2.5,
                overflowX: isPhone ? "visible" : "auto",
                overflowY: isPhone ? "visible" : "visible",
                pb: 1,
                px: isPhone ? 0 : 0.5,
                scrollSnapType: isPhone ? "none" : "x mandatory",
                scrollBehavior: "smooth",
                // Center 1-2 cards
                ...(!isPhone && offers.length <= 2 && {
                  justifyContent: "center",
                }),
                "&::-webkit-scrollbar": { height: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent", borderRadius: "3px" },
                "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "3px", "&:hover": { background: "#94A3B8" } },
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E1 transparent",
              }}
            >
              {offers.map((offer, index) => (
                <OfferCompareCard
                  key={offer.id}
                  offer={offer}
                  serviceRequestId={serviceRequestId}
                  translate={translate}
                  navigate={navigate}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  actionLoading={actionLoading}
                  index={index}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};

export default CompareOffersDialog;
